import React, { useMemo } from 'react';
import { Check } from '../../components/CustomIcons';
import { PathwayRail } from './primitives';
import { useProtoState } from './learnerData';
import {
  Screen1A_Intake, Screen1B_Modules,
  Screen2A_ModuleOverview, Screen2B_Lesson,
  Screen2E_KnowledgeCheck, Screen2F_CredentialProgress,
  Screen3A_Employers, Screen3B_EmployerDetail,
  Screen3C_Toolkit, Screen3D_Completion,
} from './learnerScreens';
import { GoalScreen, JournalView } from './engagement';

/* ============================================================
   Tablet chrome + learner flow hook
   ============================================================ */

export const TabletFrame = ({ children, railProps, orientation = 'portrait' }) => {
  const isLandscape = orientation === 'landscape';
  return (
    <div
      className="relative mx-auto proto-tablet-shell"
      style={{
        width: isLandscape ? 'min(820px, 96vw)' : 'min(500px, 94vw)',
        maxWidth: isLandscape ? '820px' : '500px',
      }}
      data-testid="proto-tablet-frame"
      data-orientation={orientation}
    >
      {/* Bezel */}
      <div
        className="relative rounded-[36px] p-3"
        style={{
          background: 'linear-gradient(145deg, #1c1c1e 0%, #0e0e10 100%)',
          boxShadow:
            '0 40px 80px -20px rgba(15,25,45,0.35), 0 20px 40px -15px rgba(15,25,45,0.25), inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.5)',
        }}
      >
        {/* Inner metallic bezel ring */}
        <div
          className="rounded-[28px] p-[3px]"
          style={{ background: 'linear-gradient(180deg, #2a2a2c 0%, #141416 100%)' }}
        >
          {/* Screen */}
          <div
            className="relative rounded-[24px] overflow-hidden proto-tablet-screen"
            style={{
              width: '100%',
              aspectRatio: isLandscape ? '1440 / 900' : '884 / 1464',
              maxHeight: isLandscape ? '520px' : '780px',
              background: '#fafaf9',
            }}
          >
            {/* Speaker + camera */}
            <div
              className="absolute z-30 flex items-center gap-3 pointer-events-none"
              style={
                isLandscape
                  ? { left: 4, top: '50%', transform: 'translateY(-50%) rotate(90deg)', transformOrigin: 'left center' }
                  : { top: 2, left: '50%', transform: 'translateX(-50%)' }
              }
            >
              <div className="w-1.5 h-1.5 rounded-full bg-black/40" />
              <div className="w-16 h-1 rounded-full bg-black/25" />
              <div className="w-1.5 h-1.5 rounded-full bg-black/40" />
            </div>
            <div className="flex flex-col h-full relative">
              {railProps && <PathwayRail {...railProps} />}
              {children}
            </div>
          </div>
        </div>
        {/* Bottom home indicator */}
        <div className="mt-2 flex justify-center">
          <div className="w-14 h-1 rounded-full bg-white/25" />
        </div>
      </div>
      {/* Side power/volume hints */}
      <div className="absolute right-[-4px] top-[110px] w-1 h-16 rounded-r bg-black/60" />
      <div className="absolute left-[-4px] top-[90px] w-1 h-10 rounded-l bg-black/60" />
      <div className="absolute left-[-4px] top-[130px] w-1 h-14 rounded-l bg-black/60" />
    </div>
  );
};

export const OrientationToggle = ({ orientation, onChange }) => (
  <div className="flex flex-col items-center gap-1.5 mt-4" data-testid="proto-orientation-toggle">
    <div className="inline-flex items-center gap-1 rounded-full p-1" style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
      {[
        { k: 'portrait', label: 'Portrait', w: 12, h: 18 },
        { k: 'landscape', label: 'Landscape', w: 18, h: 12 },
      ].map((it) => {
        const active = orientation === it.k;
        return (
          <button
            key={it.k}
            onClick={() => onChange(it.k)}
            data-testid={`proto-orient-${it.k}`}
            aria-pressed={active}
            aria-label={`Switch to ${it.label.toLowerCase()} orientation`}
            className="inline-flex items-center justify-center rounded-full transition-all"
            style={{
              width: 42, height: 32,
              background: active ? '#f97316' : 'transparent',
              color: active ? '#0f2744' : '#94a3b8',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x={(24 - it.w) / 2} y={(24 - it.h) / 2} width={it.w} height={it.h} rx="2" stroke="currentColor" strokeWidth="2" />
              <circle cx={12} cy={it.k === 'portrait' ? 20 : 12} r="0.9" fill="currentColor" style={{ display: it.k === 'landscape' ? 'none' : 'block' }} />
            </svg>
          </button>
        );
      })}
    </div>
    <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: '#64748b', letterSpacing: '0.06em' }}>Rotate View</div>
  </div>
);

export const StepIndicator = ({ step }) => {
  const map = { '1A': 1, '1B': 1, '2A': 2, '2B': 2, '2E': 2, '2F': 2, '3A': 3, '3B': 3, '3C': 3, '3D': 3 };
  const active = map[step] || 1;
  const items = [
    { n: 1, label: 'Onboarding' },
    { n: 2, label: 'Learning' },
    { n: 3, label: 'Employer Match' },
  ];
  return (
    <nav aria-label="Prototype flow progress" className="flex items-center gap-2 justify-center">
      {items.map((it, i) => (
        <React.Fragment key={it.n}>
          <div
            className="flex items-center gap-2 rounded-full px-3 py-1.5 text-[13px] font-semibold"
            style={
              active === it.n
                ? { background: '#f97316', color: '#0f2744' }
                : active > it.n
                ? { background: 'rgba(21,128,61,0.12)', color: '#15803d' }
                : { background: '#e2e8f0', color: '#64748b' }
            }
            aria-current={active === it.n ? 'step' : undefined}
          >
            <span
              className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold"
              style={
                active === it.n
                  ? { background: '#0f2744', color: '#f97316' }
                  : active > it.n
                  ? { background: '#15803d', color: '#ffffff' }
                  : { background: '#ffffff', color: '#64748b' }
              }
              aria-hidden="true"
            >
              {active > it.n ? <Check className="w-2.5 h-2.5" /> : it.n}
            </span>
            <span className="hidden sm:inline">{it.label}</span>
          </div>
          {i < items.length - 1 && <div className="w-4 h-px" style={{ background: '#cbd5e1' }} aria-hidden="true" />}
        </React.Fragment>
      ))}
    </nav>
  );
};

export const useLearnerFlow = ({ onOpenChallenge, onOpenSettings, onOpenJournal, journalOpen, onCloseJournal }) => {
  const { state, update, reset } = useProtoState();

  const screen = useMemo(() => {
    if (journalOpen) return <JournalView onBack={onCloseJournal} />;
    const props = { state, update, reset };
    const homeProps = { ...props, onOpenChallenge, onOpenSettings, onOpenJournal };
    switch (state.step) {
      case '1A': return <Screen1A_Intake {...props} />;
      case '1B': return <Screen1B_Modules {...homeProps} />;
      case '1C': return <GoalScreen onContinue={() => update({ step: '2A' })} onBack={() => update({ step: '1B' })} />;
      case '2A': return <Screen2A_ModuleOverview {...props} />;
      case '2B': return <Screen2B_Lesson {...props} />;
      case '2E': return <Screen2E_KnowledgeCheck {...props} />;
      case '2F': return <Screen2F_CredentialProgress {...props} />;
      case '3A': return <Screen3A_Employers {...props} />;
      case '3B': return <Screen3B_EmployerDetail {...props} />;
      case '3C': return <Screen3C_Toolkit {...props} />;
      case '3D': return <Screen3D_Completion {...props} />;
      default: return <Screen1A_Intake {...props} />;
    }
  }, [state, journalOpen, onOpenChallenge, onOpenSettings, onOpenJournal, onCloseJournal, update, reset]);

  const railSteps = ['2A', '2B', '2E', '2F'];
  const railProps = !journalOpen && railSteps.includes(state.step) && state.moduleId
    ? {
        moduleId: state.moduleId,
        currentLessonIdx: state.currentLessonIdx,
        completedLessons: state.completedLessons[state.moduleId] || [],
      }
    : null;

  return { content: screen, railProps, step: state.step };
};
